import {computed, DestroyRef, inject, Injectable} from '@angular/core';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {concat, distinctUntilChanged, first, map} from 'rxjs';
import {AccountModeService} from 'src/app/core/entities/account/account-modes/account-mode.service';
import {ACCOUNT_MODE} from 'src/app/core/entities/account/account-modes/enum/account-mode.enum';
import {AccountQrManagerService} from 'src/app/core/entities/account/account-qr-manager/account-qr-manager.service';
import {AccountSelectedEvent} from 'src/app/shared/application/events/account/account-selected.event';
import {ScannerService} from 'src/app/core/services/scanner/scanner.service';
import {ACCOUNT_SIDE_VIEW_TYPE} from 'src/app/shared/services/layout/enum/account-view-type.enum';
import {LayoutService} from 'src/app/shared/services/layout/layout.service';
import {IQuickActionsWidgetViewModel} from './quick-actions-widget.viewmodel.interface';
import {BookingUpdatedEvent} from '../../../application/events/booking/booking-updated.event';
import {EVENT_BUS} from "../../../domain/tokens/event-bus.token";
import {FeatureFlagAdapter} from "@shared/infrastructure/adapters/feature-flag.adapter";

@Injectable({providedIn: 'root'})
export class QuickActionsWidgetViewModel implements IQuickActionsWidgetViewModel {
  private readonly layoutService = inject(LayoutService);
  private readonly eventBus = inject(EVENT_BUS);
  private readonly scannerService = inject(ScannerService);
  private readonly featureFlagService = inject(FeatureFlagAdapter);
  private readonly accountModeService = inject(AccountModeService);
  private readonly accountQRManagerService = inject(AccountQrManagerService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly selectedAccountEntries = toSignal(
    this.eventBus.ofType(AccountSelectedEvent).pipe(
      map((event) => event.account?.entries)
    ),
    {initialValue: null}
  );

  private readonly featureFlags = toSignal(
    this.featureFlagService.onFeatureFlagsLoaded$.pipe(
      map(() => this.featureFlagService.featureFlags)
    ),
    {initialValue: null}
  );

  readonly activeBooking = toSignal(
    this.eventBus.ofType(BookingUpdatedEvent).pipe(
      map(event => event.booking),
      takeUntilDestroyed(this.destroyRef)
    ),
    {initialValue: undefined}
  );

  readonly viewType = toSignal(this.layoutService.onAccountSideViewChanged$, {
    initialValue:
      this.layoutService.accountSideView ?? ACCOUNT_SIDE_VIEW_TYPE.left,
  });

  readonly accountMode = toSignal(
    concat(
      this.accountModeService.getAccountMode(),
      this.accountModeService.onAccountModeChanged$
    ).pipe(
      distinctUntilChanged()
    ),
    {initialValue: ACCOUNT_MODE.default}
  );

  readonly quickActions = toSignal(
    this.layoutService.onQuickActionsChanged$.pipe(
      map((actions) => actions ?? undefined)
    ),
    {
      initialValue: undefined,
    }
  );

  readonly isRightView = computed<boolean>(() => {
    return this.viewType() === ACCOUNT_SIDE_VIEW_TYPE.right;
  });

  readonly disableScanQR = computed<boolean>(() => {
    const noEntriesSelected = !this.selectedAccountEntries()?.length;
    const isAllowedMode = [ACCOUNT_MODE.default, ACCOUNT_MODE.initial].includes(this.accountMode());

    return !noEntriesSelected || !isAllowedMode;
  });

  readonly showScanQR = computed((): boolean => {
    if (!this.cameraDevices()?.length) {
      return false;
    }

    if (this.activeBooking()) {
      return false;
    }

    return !!this.quickActions()?.qrScan;
  });

  readonly cameraDevices = toSignal(this.scannerService.getCameraDevices(), {
    initialValue: [],
  });

  // TODO: Move to a use case
  processManualScanQR(): void {
    this.scannerService
      .manualScan()
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe((qrCode: string) => {
        this.accountQRManagerService.manageAccountQRScan(qrCode);
      });
  }
}
